/**
 * Colours that have to be settled before an order goes to the supplier.
 *
 * A material order with no colour on the shingles is not an order, it is a
 * phone call — and the call happens two days later when the delivery is
 * already late. The gate is here rather than in anyone's head.
 *
 * Which products need one is a rule on the catalogue (`products.color_rule`),
 * not a list in this file. There are three hundred products and the list grows;
 * the day something new needs a colour it should be set in Tools, not shipped.
 *
 *   required     a real colour must be chosen — shingles, metal panels, drip edge
 *   brown_black  must be chosen, and it is one of two
 *   match        colour matched to the roof; filled in rather than asked
 */

export type ColorRule = "required" | "brown_black" | "match";

/** What "match" writes on the line. Recorded so the order says it explicitly. */
export const COLOR_MATCH = "Color Match";

export const BROWN_BLACK: readonly string[] = ["Brown", "Black"];

export interface ColorProduct {
  id: string;
  name?: string | null;
  color_rule?: string | null;
  color_options?: readonly string[] | null;
}

export interface ColorLine {
  id: string;
  product_id: string | null;
  name?: string | null;
  description?: string | null;
  color?: string | null;
}

/** One line that cannot go to the supplier yet, and why. */
export interface MissingColor {
  itemId: string;
  description: string;
  /** What the person has to choose between, when the choice is fixed. */
  options: readonly string[];
}

export function colorRuleFor(
  product: ColorProduct | undefined,
): ColorRule | null {
  const rule = product?.color_rule;
  return rule === "required" || rule === "brown_black" || rule === "match"
    ? rule
    : null;
}

/** The colours offered for a product, if the choice is a fixed one. */
export function colorOptionsFor(
  product: ColorProduct | undefined,
): readonly string[] {
  const rule = colorRuleFor(product);
  if (rule === "match") return [COLOR_MATCH];
  const listed = product?.color_options ?? [];
  if (listed.length > 0) return listed;
  return rule === "brown_black" ? BROWN_BLACK : [];
}

/**
 * The default a line can be given without asking anyone.
 *
 * Only "match" has one. A shingle colour is the customer's choice and guessing
 * it would be worse than blocking, which is the entire point of the gate.
 */
export function defaultColorFor(
  product: ColorProduct | undefined,
): string | null {
  return colorRuleFor(product) === "match" ? COLOR_MATCH : null;
}

/**
 * Lines that still need a colour before this order can be placed.
 *
 * Empty means the order is good to go. A line whose product has been removed
 * from the catalogue is not blocked — there is no rule left to apply, and
 * holding an order hostage to a deleted product helps nobody.
 */
export function missingColors(
  lines: readonly ColorLine[],
  products: readonly ColorProduct[],
): MissingColor[] {
  const byId = new Map(products.map((p) => [p.id, p]));
  const missing: MissingColor[] = [];

  for (const line of lines) {
    if (!line.product_id) continue;
    const product = byId.get(line.product_id);
    if (!product) continue;

    const rule = colorRuleFor(product);
    if (rule === null || rule === "match") continue;
    if ((line.color ?? "").trim() !== "") continue;

    missing.push({
      itemId: line.id,
      description:
        line.name ?? line.description ?? product.name ?? "(unnamed line)",
      options: colorOptionsFor(product),
    });
  }

  return missing;
}
